import { ComponentType, lazy } from 'react';

import { ROUTE_PATHS } from '@/config/constants';
import { Role } from '@/contexts/AuthContext';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DefaultLayout } from '@/layouts/DefaultLayout';
import { AdminAuditLogs } from '@/pages/admin/AuditLogs';
import { AdminCustomers } from '@/pages/admin/Customers';
import { AdminCustomersCreate } from '@/pages/admin/Customers/CustomersCreate';
import { AdminCustomersEdit } from '@/pages/admin/Customers/CustomersEdit';
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { AdminModels } from '@/pages/admin/Models';
import { AdminOnBoarding } from '@/pages/admin/OnBoarding';
import { AdminPrompts } from '@/pages/admin/Prompts';
import { AdminSettings } from '@/pages/admin/Settings';
import { AdminTaxonomy } from '@/pages/admin/Taxonomy';
import { AdminUsers } from '@/pages/admin/Users';
import { AdminUsersCreate } from '@/pages/admin/Users/UsersCreate';
import { AdminUsersEdit } from '@/pages/admin/Users/UsersEdit';
import { AdminVoiceToneExports } from '@/pages/admin/VoiceTone';
import { Analytics } from '@/pages/Analytics';
import { Audiences } from '@/pages/Audiences';
import { Login } from '@/pages/auth/Login';
import { Logout } from '@/pages/auth/Logout';
import { AuthCallback } from '@/pages/AuthCallback';
import { Home } from '@/pages/Home';
import { MerchandisingCollections } from '@/pages/MerchandisingCollections';
import { Collection as MerchandisingCollection } from '@/pages/MerchandisingCollections/pages/Collection';
import { Slice as MerchandisingCollectionsSlice } from '@/pages/MerchandisingCollections/pages/Slice';
import { NotFound } from '@/pages/NotFound';
import { Personalization } from '@/pages/Personalization';
import { Profiles } from '@/pages/Profiles';
import { SeoContent } from '@/pages/SeoContent';
import { Settings } from '@/pages/Settings';
import { StylistAi } from '@/pages/StylistAi';
import { Taxonomy } from '@/pages/Taxonomy';
import { VisionEnrichment } from '@/pages/VisionEnrichment';
import { VisionProduct } from '@/pages/VisionProduct';

const Copilot = lazy(() => import('@/pages/Copilot'));
const AudiencesCreate = lazy(() => import('@/pages/AudiencesCreate'));

export type RouteItem = {
  path: string;
  Component: ComponentType;
  Layout?: ComponentType;
  enabled: boolean;
  isPrivate?: boolean;
  roles?: Role[];
};

export const routes: RouteItem[] = [
  // Start user routes
  {
    path: ROUTE_PATHS.HOME,
    Component: Home,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.LOGOUT,
    Component: Logout,
    isPrivate: true,
    enabled: true,
  },
  {
    path: ROUTE_PATHS.COPILOT,
    Component: Copilot,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: false,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.SETTINGS,
    Component: Settings,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.VISION_ENRICHMENT,
    Component: VisionEnrichment,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: `${ROUTE_PATHS.VISION_PRODUCT}`,
    Component: VisionProduct,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.TAXONOMY,
    Component: Taxonomy,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.PERSONALIZATION,
    Component: Personalization,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.AUDIENCES,
    Component: Audiences,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.AUDIENCES_CREATE,
    Component: AudiencesCreate,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.SEO_CONTENT,
    Component: SeoContent,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.ANALYTICS,
    Component: Analytics,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: false,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.STYLIST_AI,
    Component: StylistAi,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: false,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.AUTH_CALLBACK,
    Component: AuthCallback,
    isPrivate: true,
    enabled: true,
  },
  {
    path: ROUTE_PATHS.MERCHANDISING_COLLECTIONS,
    Component: MerchandisingCollections,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.MERCHANDISING_COLLECTION,
    Component: MerchandisingCollection,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.MERCHANDISING_COLLECTIONS_SLICE,
    Component: MerchandisingCollectionsSlice,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  {
    path: ROUTE_PATHS.PROFILES,
    Component: Profiles,
    Layout: DefaultLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root, Role.Admin, Role.User],
  },
  // End user routes

  // Start admin routes
  {
    path: ROUTE_PATHS.ADMIN_DASHBOARD,
    Component: AdminDashboard,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_CUSTOMERS,
    Component: AdminCustomers,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_CUSTOMERS_CREATE,
    Component: AdminCustomersCreate,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_CUSTOMERS_EDIT,
    Component: AdminCustomersEdit,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_ONBOARDING,
    Component: AdminOnBoarding,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_MODELS,
    Component: AdminModels,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_SETTINGS,
    Component: AdminSettings,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_USERS,
    Component: AdminUsers,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_USERS_CREATE,
    Component: AdminUsersCreate,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_TAXONOMY,
    Component: AdminTaxonomy,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_USERS_EDIT,
    Component: AdminUsersEdit,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_AUDIT_LOGS,
    Component: AdminAuditLogs,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_PROMPTS,
    Component: AdminPrompts,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  {
    path: ROUTE_PATHS.ADMIN_VOICE_TONE_EXPORTS,
    Component: AdminVoiceToneExports,
    Layout: AdminLayout,
    isPrivate: true,
    enabled: true,
    roles: [Role.Root],
  },
  // End admin routes

  // Start public routes
  {
    path: ROUTE_PATHS.LOGIN,
    Component: Login,
    Layout: AuthLayout,
    enabled: true,
    isPrivate: false,
  },
  // End public routes

  // Start other routes
  {
    path: ROUTE_PATHS.NOT_FOUND,
    Component: NotFound,
    enabled: true,
  },
  // End other routes
];
